/**
 * Strict numeric input parser for office money/weight forms.
 *
 * Replaces `parseFloat` which silently coerces malformed strings:
 *   parseFloat("4,2")        → 4
 *   parseFloat("10foo")      → 10
 *   parseFloat("4.2e1")      → 42
 *   parseFloat("")           → NaN
 *   parseFloat(" 4.2")       → 4.2  (leading whitespace tolerated)
 *   parseFloat("0x10")       → 0   (hex prefix)
 *
 * The strict grammar rejects all of these. The raw trimmed string must
 * match DECIMAL_RE exactly:
 *   - one or more digits
 *   - optionally one decimal point followed by one or more digits
 *   - NO sign, NO thousands separators, NO scientific notation, NO
 *     trailing/leading garbage, NO empty string.
 *
 * Returns `NaN` on any non-match — callers MUST compare with `isNaN`
 * (NOT `!==` or `||` — see Bug-Zero rule "check type, not truthiness"
 * because `NaN` is a number). The form's field error is the right
 * place to surface the message.
 *
 * `parseAmount` parses a dollars string (the office UI captures dollars
 * with two decimals) and returns the integer cents. The strict grammar
 * check applies to the input string BEFORE the cents conversion so a
 * malformed value never reaches `Math.round`.
 */
export const DECIMAL_RE = /^\d+(\.\d+)?$/;

/**
 * Max weight in tons the persistence layer accepts.
 *
 * The `jobs.weight_tons` column is `numeric(10,2)` — 10 total digits, 2
 * after the decimal point, so the maximum representable value is
 * 99,999,999.99. Anything larger either rounds silently (loss of
 * precision) or overflows at the persistence layer, depending on the
 * Postgres coercion path. UI rejects values above this with
 * "Amount too large" so the user gets a clean field error instead of
 * a 500 from the server.
 *
 * The cents fields use `Number.isSafeInteger` for the equivalent bound
 * (and `Number.isFinite` for NaN/Infinity). Tons do NOT use
 * `isSafeInteger` because fractional weights (4.2, 0.5) are legitimate
 * and `isSafeInteger(4.2) === false` would reject every legit entry.
 * The numeric(10,2) column is the right bound for the tons field.
 */
export const MAX_TONS = 99999999.99;

/**
 * Parse a decimal-string input. Returns NaN if it doesn't match
 * DECIMAL_RE, otherwise returns `Number(raw)`.
 *
 * Trim is intentionally NOT applied here — the regex is exact. A
 * trailing space from a paste is a real input bug we want to surface
 * ("Enter a number"), not silently tolerate. Form inputs in the
 * office UI use `type="number"` which prevents most paste issues.
 */
export function parseDecimal(raw: string): number {
  if (typeof raw !== "string" || !DECIMAL_RE.test(raw)) return NaN;
  return Number(raw);
}

/**
 * Parse a dollars input into integer cents. Returns NaN on malformed
 * input (see `parseDecimal` for grammar).
 *
 * Note: `Math.round` on a 2-decimal `Number` is safe within the
 * office-entered range (≤ $1,000,000.00). For larger values the
 * multiplication could introduce float noise — out of scope for
 * Eagle's billing.
 *
 * Magnitude contract (added per Unit #17 send-back #2): callers MUST
 * validate the returned cents with `Number.isSafeInteger(cents)` AND
 * `Number.isFinite(dollars)` BEFORE sending to the server. The
 * conversion itself does not enforce this — a 14-digit input like
 * "99999999999999" parses fine (regex matches) but `dollars * 100`
 * overflows the IEEE-754 safe-integer range (2^53 - 1 ≈ 9.007e15)
 * and `Math.round` produces a non-integer (loss of precision in the
 * final digits). The form's "Amount too large" error path catches it.
 *
 * Float-multiplication bound: 12 digits (maxLength on money inputs)
 * keeps `dollars * 100` well inside the safe-integer range
 * (12 digits → up to 9.99e11, vs. safe-integer ceiling 9.007e15).
 * `parseDollarsToCents` does not enforce maxLength — that's a form-
 * level concern. The function trusts its input shape; the form
 * layer caps the digits.
 */
export function parseDollarsToCents(raw: string): number {
  const dollars = parseDecimal(raw);
  if (isNaN(dollars)) return NaN;
  return Math.round(dollars * 100);
}
